import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Tooltip,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  LinearProgress,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import CalculateIcon from "@mui/icons-material/Calculate";
import ScienceIcon from "@mui/icons-material/Science";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TuneIcon from "@mui/icons-material/Tune";
import SettingsIcon from "@mui/icons-material/Settings";
import ReportsIcon from "@mui/icons-material/Description";
import AiIcon from "@mui/icons-material/AutoAwesome";
import EngineeringIcon from "@mui/icons-material/Engineering";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import SpaIcon from "@mui/icons-material/Spa";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import InventoryIcon from "@mui/icons-material/Inventory";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import LogoutIcon from "@mui/icons-material/Logout";
import ContactSupportIcon from "@mui/icons-material/Email";
import GavelIcon from "@mui/icons-material/Gavel";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SchemaIcon from "@mui/icons-material/AccountTree";
import AssessmentIcon from "@mui/icons-material/Assessment";
import WidgetsIcon from "@mui/icons-material/Widgets";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { config } from "../../../assets/config";
import { getCommoditiesForTower } from "../../../utils/commodityConfig";

const baseMenuItems = [
  { label: "Home", icon: <HomeIcon />, path: "/" },
  // { label: "F&B Costing", icon: <RestaurantIcon />, path: "/Landing-Page" },
  { label: "Mechanical", icon: <PrecisionManufacturingIcon />, path: "/mechanical" },
  { label: "Electronics", icon: <DeveloperBoardIcon />, path: "/electronics" },
  { label: "Packaging", icon: <InventoryIcon />, path: "/packaging" },
  { label: "F&B", icon: <RestaurantIcon />, path: "/fnb" },
  { label: "BPCH", icon: <SpaIcon />, path: "/bpch" },
  // { label: "Costing Reports", icon: <ReportsIcon />, path: "/costAnalysisReports" },
  // { label: "PlayBook", icon: <TuneIcon />, path: "/playbook" },
];

const knownIssues = [
  "AI-generated results may vary for similar inputs",
  "AI-heavy workflows may have variable response times",
  "Limited input details may produce broader estimates",
  "Non-standard product configurations may require additional validation",
  "Costing model may be improved as more business scenarios are tested",
];

const helpGifItems = [
  {
    title: "Select Commodity",
    description: "Choose a commodity from My Agents to begin costing.",
    src: "/help/SelectCommodity.gif",
    durationMs: 8650,
  },
  {
    title: "Quick Costing",
    description: "Walkthrough for starting and running Quick Costing.",
    src: "/help/QuickCosting.gif",
    durationMs: 17450,
  },
  {
    title: "Cost Breakdown",
    description: "Review the generated cost breakdown.",
    src: "/help/CostBreakdown.gif",
    durationMs: 10450,
  },
  {
    title: "Formulation",
    description: "Review generated formulation or BOM details.",
    src: "/help/Formulation.gif",
    durationMs: 10750,
  },
  {
    title: "Playbook",
    description: "Review and adjust assumptions used for costing.",
    src: "/help/Playbook.gif",
    durationMs: 11550,
  },
  {
    title: "Risk Analysis",
    description: "Review risks highlighted for the costing output.",
    src: "/help/RiskAnalysis.gif",
    durationMs: 4350,
  },
  {
    title: "Substitute Analysis",
    description: "Review substitute options and related analysis.",
    src: "/help/SubstituteAnalysis.gif",
    durationMs: 4800,
  },
];

const Sidebar = () => {
  // Initialize expanded state from localStorage, default to false
  const [expanded, setExpanded] = useState(() => {
    const savedState = localStorage.getItem("sidebarExpanded");
    return savedState === "true";
  });
  const [isHovered, setIsHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [testFbAnchorEl, setTestFbAnchorEl] = useState(null);
  const [testMechAnchorEl, setTestMechAnchorEl] = useState(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [knownIssuesDialogOpen, setKnownIssuesDialogOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [selectedHelpGifIndex, setSelectedHelpGifIndex] = useState(0);
  const [helpGifLoadError, setHelpGifLoadError] = useState(false);
  const [helpGifReplayKey, setHelpGifReplayKey] = useState(0);
  const [helpGifProgress, setHelpGifProgress] = useState(0);
  const helpGifProgressTimerRef = React.useRef(null);
  const [supportMessage, setSupportMessage] = useState("");
  const [supportNotification, setSupportNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [userInfo, setUserInfo] = useState({
    username: "User",
    email: "user@example.com",
    initials: "-",
    isAdmin: false,
    role: "",
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const appVersion = "1.1.0";
  const selectedHelpGif = helpGifItems[selectedHelpGifIndex] || helpGifItems[0];
  const selectedHelpGifSrc = selectedHelpGif
    ? `${selectedHelpGif.src}?replay=${helpGifReplayKey}`
    : "";
  const viewAccessList = Array.isArray(user?.viewAccess) ? user.viewAccess : [];
  const applyViewFilter = Array.isArray(user?.viewAccess);
  const towerLabels = new Set(["Mechanical", "Electronics", "Packaging", "F&B", "BPCH"]);
  const hasTowerView = (label) => {
    if (!applyViewFilter) return true;
    return viewAccessList.some((key) => key.startsWith(`${label} - `));
  };
  const menuItems = baseMenuItems.filter(
    (item) => !towerLabels.has(item.label) || hasTowerView(item.label),
  );
  const fbTestCommodities = getCommoditiesForTower("FB");
  const mechTestCommodities = ["Plastics", "Sheet Metal", "Casting", "Forging"];
  const normalizedRole = String(userInfo.role || user?.role || "")
    .trim()
    .toLowerCase();
  const isSuperAdmin = normalizedRole === "super admin";
  const showAdminSection = (userInfo.isAdmin && normalizedRole !== "end user") || isSuperAdmin;
  const showTestsSection = normalizedRole !== "end user" || isSuperAdmin;

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUserSettings = () => {
    handleMenuClose();
    setUserDetailsOpen(true);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate("/login");
  };

  const handleSettingsMenuOpen = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsAnchorEl(null);
    setTestFbAnchorEl(null);
    setTestMechAnchorEl(null);
  };

  const handleUserDetailsClose = () => {
    setUserDetailsOpen(false);
  };

  const handleAdminSchemaManagement = () => {
    handleSettingsMenuClose();
    navigate("/admin/schema-management");
  };

  const handleAdminSchemaManagementV2 = () => {
    handleSettingsMenuClose();
    navigate("/admin/schema-management-v2");
  };

  const handleAdminSchemaManagementV3 = () => {
    handleSettingsMenuClose();
    navigate("/admin/schema-management-v3");
  };

  const handleConstraintsMaster = () => {
    handleSettingsMenuClose();
    navigate("/admin/constraints-master");
  };

  const handleUserAccessManagement = () => {
    handleSettingsMenuClose();
    navigate("/admin/user-management");
  };

  const handleAppWorkspace = () => {
    handleSettingsMenuClose();
    navigate("/admin/app-workspace");
  };

  const handleContactSupport = () => {
    setSupportDialogOpen(true);
  };

  const handleOpenKnownIssues = () => {
    setKnownIssuesDialogOpen(true);
  };

  const handleCloseKnownIssues = () => {
    setKnownIssuesDialogOpen(false);
  };

  const stopHelpGifProgress = React.useCallback(() => {
    if (helpGifProgressTimerRef.current) {
      clearInterval(helpGifProgressTimerRef.current);
      helpGifProgressTimerRef.current = null;
    }
  }, []);

  const startHelpGifProgress = React.useCallback((initialProgress = 0) => {
    stopHelpGifProgress();

    const durationMs = selectedHelpGif?.durationMs || 15000;
    const clampedProgress = Math.min(Math.max(initialProgress, 0), 100);
    setHelpGifProgress(clampedProgress);

    const startedAt = Date.now() - (clampedProgress / 100) * durationMs;
    helpGifProgressTimerRef.current = setInterval(() => {
      const elapsedMs = (Date.now() - startedAt) % durationMs;
      const nextProgress = (elapsedMs / durationMs) * 100;
      setHelpGifProgress(nextProgress);
    }, 100);
  }, [selectedHelpGif?.durationMs, stopHelpGifProgress]);

  const handleOpenHelp = () => {
    setHelpDialogOpen(true);
    setHelpGifLoadError(false);
    setHelpGifProgress(0);
  };

  const handleCloseHelp = () => {
    setHelpDialogOpen(false);
    stopHelpGifProgress();
  };

  const handleSelectHelpGif = (index) => {
    setSelectedHelpGifIndex(index);
    setHelpGifLoadError(false);
    setHelpGifProgress(0);
    setHelpGifReplayKey((currentKey) => currentKey + 1);
  };

  const handleReplayHelpGif = () => {
    setHelpGifLoadError(false);
    setHelpGifProgress(0);
    setHelpGifReplayKey((currentKey) => currentKey + 1);
  };

  const handleCloseSupportDialog = () => {
    setSupportDialogOpen(false);
    setSupportMessage("");
  };

  const handleSendSupportRequest = async () => {
    if (!supportMessage.trim()) {
      setSupportNotification({
        open: true,
        message: "Please enter a message before sending.",
        severity: "warning",
      });
      return;
    }

    try {
      const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
      const url = `${config.VITE_API_BASE_URL || ""}/api/admin/contact-support`;

      console.log("📧 Sending support request to:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_name: fullName || userInfo.username || "N/A",
          user_email: user?.email || userInfo.email || "",
          message: supportMessage.trim(),
          request_type: "general_support",
        }),
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.error("❌ Server returned non-JSON response");
        setSupportNotification({
          open: true,
          message:
            "Failed to send support request. Please try again or email support@kearney.com directly.",
          severity: "error",
        });
        handleCloseSupportDialog();
        return;
      }

      const result = await response.json();

      if (response.ok) {
        console.log("✅ Support request sent:", result);
        setSupportNotification({
          open: true,
          message: "Support request sent! We'll get back to you soon.",
          severity: "success",
        });
        handleCloseSupportDialog();
      } else {
        console.error("❌ Support request failed:", result);
        setSupportNotification({
          open: true,
          message: result.error || "Failed to send support request. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("❌ Support request error:", error);
      setSupportNotification({
        open: true,
        message: "Network error. Please try again or email support@kearney.com directly.",
        severity: "error",
      });
    }
  };

  const handleCloseNotification = () => {
    setSupportNotification({ ...supportNotification, open: false });
  };

  const handleApiRunLogs = () => {
    handleSettingsMenuClose();
    navigate("/admin/api-run-logs");
  };

  const handleAdminAnalysis = () => {
    handleSettingsMenuClose();
    navigate("/admin/analysis");
  };

  const handleAdminTrace = () => {
    handleSettingsMenuClose();
    navigate("/admin/trace");
  };

  const handleCalculationResults = () => {
    handleSettingsMenuClose();
    navigate("/results");
  };

  const handleTestFbMenuOpen = (event) => {
    setTestFbAnchorEl(event.currentTarget);
  };

  const handleTestFbMenuClose = () => {
    setTestFbAnchorEl(null);
  };

  const handleTestMechMenuOpen = (event) => {
    setTestMechAnchorEl(event.currentTarget);
  };

  const handleTestMechMenuClose = () => {
    setTestMechAnchorEl(null);
  };

  const handleTestCommoditySelect = (commodity) => {
    handleTestFbMenuClose();
    handleSettingsMenuClose();
    navigate(`/test/form-fb?tower=FB&commodity=${encodeURIComponent(commodity)}`);
  };

  const handleTestMechCommoditySelect = (commodity) => {
    handleTestMechMenuClose();
    handleSettingsMenuClose();
    if (commodity === "Plastics") {
      navigate("/test/form-mech/plastics");
    }
  };

  // Save expanded state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("sidebarExpanded", expanded.toString());
  }, [expanded]);

  useEffect(() => {
    if (!user) return;

    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    const profileImage = user.userImage || "";

    const email = user.email || "user@example.com";

    let initials = "-";
    if (firstName || lastName) {
      initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();
    } else if (email && typeof email === "string") {
      initials = email.substring(0, 2).toUpperCase();
    }

    const roleName =
      Array.isArray(user.role) && user.role.length > 0
        ? user.role[0]?.role_name || ""
        : user.role || "";

    setUserInfo({
      username: fullName || email || "User",
      email,
      initials: initials || "U",
      isAdmin: !!user.isAdmin,
      role: roleName || "",
      profileImage: profileImage || "",
    });
  }, [user]);

  const handleMenuClick = (item) => {
    if (item.path) {
      navigate(item.path);
    }
  };

  useEffect(() => {
    return () => {
      stopHelpGifProgress();
    };
  }, [stopHelpGifProgress]);

  useEffect(() => {
    const handleOpenHelpGuide = (event) => {
      const requestedIndex = Number(event.detail?.index ?? 0);
      const nextIndex =
        Number.isInteger(requestedIndex) && requestedIndex >= 0 && requestedIndex < helpGifItems.length
          ? requestedIndex
          : 0;

      setSelectedHelpGifIndex(nextIndex);
      setHelpGifLoadError(false);
      setHelpGifProgress(0);
      setHelpGifReplayKey((currentKey) => currentKey + 1);
      setHelpDialogOpen(true);
    };

    window.addEventListener("velocity-help-guide-open", handleOpenHelpGuide);
    return () => {
      window.removeEventListener("velocity-help-guide-open", handleOpenHelpGuide);
    };
  }, []);

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: expanded ? 240 : 60,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: expanded ? 240 : 60,
            position: "fixed",
            height: "100%",
            background: "#000000",
            color: "#F1F2F3",
            transition: "width 0.3s ease-in-out",
            overflowX: "visible",
            overflowY: "auto",
            borderRight: "none",
            boxShadow: "4px 0 20px rgba(0, 0, 0, 0.3)",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(0, 0, 0, 0.2)",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255, 96, 52, 0.5)",
              borderRadius: "3px",
              "&:hover": {
                background: "rgba(255, 96, 52, 0.8)",
              },
            },
          },
        }}
      >
        {/* Logo with Toggle */}
        <Box
          sx={{
            position: "fixed",
            top: 16,
            left: expanded ? 20 : 10,
            zIndex: 1300,
            transition: "left 0.3s ease-in-out",
            cursor: expanded ? "default" : "pointer",
          }}
          onClick={() => !expanded && setExpanded(true)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {expanded ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: 200,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#282727ff",
                  borderRadius: "4px",
                  padding: "6px 10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 150,
                }}
              >
                <Box
                  component="img"
                  src="/images/Logo/Kearney Perlab.jpg"
                  alt="Company Logo"
                  sx={{
                    height: 30,
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <IconButton
                onClick={() => {
                  setExpanded(false);
                  setIsHovered(false);
                }}
                sx={{
                  backgroundColor: "#ff6034",
                  color: "#000000",
                  width: 30,
                  height: 30,
                  "&:hover": {
                    backgroundColor: "#ff7b4a",
                  },
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                objectFit: "contain",
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {isHovered ? (
                <IconButton
                  sx={{
                    backgroundColor: "#ff6034",
                    color: "#000000",
                    width: 30,
                    height: 30,
                    "&:hover": {
                      backgroundColor: "#ff7b4a",
                    },
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  }}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                <Box
                  sx={{
                    backgroundColor: "#282727ff",
                    borderRadius: "4px",
                    padding: "6px 5px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 100,
                  }}
                >
                  <Box
                    component="img"
                    src="/images/Logo/K.jpg"
                    alt="Company Logo"
                    sx={{
                      height: 20,
                      // width: 15,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 5, pb: 4, pt: 4 }}>
          {/* User Profile Section */}
          <Box
            sx={{
              px: expanded ? 1 : 0,
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: expanded ? 1.5 : 0,
            }}
          >
            <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
              <Avatar
                src={userInfo.profileImage || undefined}
                sx={{
                  bgcolor: "#ff6034",
                  color: "#000000",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  width: 30,
                  height: 30,
                }}
              >
                {!userInfo.profileImage ? userInfo.initials : null}
              </Avatar>
            </IconButton>
            {expanded && (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#F1F2F3",
                    lineHeight: 1.2,
                    fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                  }}
                >
                  {userInfo.username}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#798286",
                    fontSize: "0.75rem",
                    fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                  }}
                >
                  {userInfo.email}
                </Typography>
              </Box>
            )}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={handleUserSettings}>
                <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                User settings
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>

            {/* Settings Submenu */}
            <Menu
              anchorEl={settingsAnchorEl}
              open={Boolean(settingsAnchorEl)}
              onClose={handleSettingsMenuClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              sx={{ ml: 1 }}
            >
              {showAdminSection && [
                <Typography
                  key="admin-header"
                  sx={{
                    px: 2,
                    py: 1,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#798286",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Admin
                </Typography>,
                <MenuItem key="schema-mgmt-v3" onClick={handleAdminSchemaManagementV3}>
                  <SchemaIcon fontSize="small" sx={{ mr: 1 }} />
                  Schema Management V3
                </MenuItem>,
                <MenuItem key="constraints-master" onClick={handleConstraintsMaster}>
                  <SchemaIcon fontSize="small" sx={{ mr: 1 }} />
                  Constraints Master
                </MenuItem>,
                <MenuItem key="admin-analysis" onClick={handleAdminAnalysis}>
                  <AssessmentIcon fontSize="small" sx={{ mr: 1 }} />
                  Analysis
                </MenuItem>,
                <MenuItem key="admin-trace" onClick={handleAdminTrace}>
                  <AssessmentIcon fontSize="small" sx={{ mr: 1 }} />
                  Trace
                </MenuItem>,
                <MenuItem key="user-access" onClick={handleUserAccessManagement}>
                  <AdminPanelSettingsIcon fontSize="small" sx={{ mr: 1 }} />
                  User Access Management
                </MenuItem>,
                isSuperAdmin ? (
                  <MenuItem key="app-workspace" onClick={handleAppWorkspace}>
                    <WidgetsIcon fontSize="small" sx={{ mr: 1 }} />
                    App Workspace
                  </MenuItem>
                ) : null,
                <Divider key="divider" />,
              ]}
              {showTestsSection && [
                <Divider key="tests-divider" />,
                <Typography
                  key="tests-header"
                  sx={{
                    px: 2,
                    py: 1,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#798286",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Tests
                </Typography>,
                <MenuItem key="tests-form" onClick={handleTestFbMenuOpen}>
                  <ScienceIcon fontSize="small" sx={{ mr: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography component="span" sx={{ fontSize: "0.875rem" }}>
                      Test FB
                    </Typography>
                    <ChevronRightIcon fontSize="small" sx={{ color: "#798286", ml: 2 }} />
                  </Box>
                </MenuItem>,
                <MenuItem key="tests-mech" onClick={handleTestMechMenuOpen}>
                  <EngineeringIcon fontSize="small" sx={{ mr: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography component="span" sx={{ fontSize: "0.875rem" }}>
                      TestMech
                    </Typography>
                    <ChevronRightIcon fontSize="small" sx={{ color: "#798286", ml: 2 }} />
                  </Box>
                </MenuItem>,
              ]}
              {showAdminSection && [
                <Divider key="backup-divider" />,
                <Typography
                  key="backup-header"
                  sx={{
                    px: 2,
                    py: 1,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#798286",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Backup
                </Typography>,
                <MenuItem key="schema-mgmt" onClick={handleAdminSchemaManagement}>
                  <SchemaIcon fontSize="small" sx={{ mr: 1 }} />
                  Schema Management
                </MenuItem>,
                <MenuItem key="schema-mgmt-v2" onClick={handleAdminSchemaManagementV2}>
                  <SchemaIcon fontSize="small" sx={{ mr: 1 }} />
                  Schema Management V2
                </MenuItem>,
                <MenuItem key="api-run-logs" onClick={handleApiRunLogs}>
                  <AssessmentIcon fontSize="small" sx={{ mr: 1 }} />
                  API Run Logs
                </MenuItem>,
                <MenuItem key="calc-results" onClick={handleCalculationResults}>
                  <AssessmentIcon fontSize="small" sx={{ mr: 1 }} />
                  Calculation Results
                </MenuItem>,
              ]}
              <Divider />
              <Typography
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#798286",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Version
              </Typography>
              <Typography
                sx={{
                  px: 2,
                  pt: 0.5,
                  pb: 1.5,
                  fontSize: "0.8rem",
                  color: "#A0A8AB",
                  textAlign: "left",
                }}
              >
                {`v${appVersion}`}
              </Typography>
            </Menu>
            <Menu
              anchorEl={testFbAnchorEl}
              open={Boolean(testFbAnchorEl)}
              onClose={handleTestFbMenuClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              sx={{ ml: 1 }}
            >
              {fbTestCommodities.map((commodity) => (
                <MenuItem key={commodity} onClick={() => handleTestCommoditySelect(commodity)}>
                  {commodity}
                </MenuItem>
              ))}
            </Menu>
            <Menu
              anchorEl={testMechAnchorEl}
              open={Boolean(testMechAnchorEl)}
              onClose={handleTestMechMenuClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              sx={{ ml: 1 }}
            >
              {mechTestCommodities.map((commodity) => (
                <MenuItem
                  key={commodity}
                  onClick={() => handleTestMechCommoditySelect(commodity)}
                >
                  {commodity}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Divider sx={{ mx: 2, my: 1, borderColor: "rgba(241, 242, 243, 0.08)" }}></Divider>
          <List sx={{ px: expanded ? 1 : 0 }}>
            {menuItems.map((item, index) => (
              <Tooltip
                title={expanded ? "" : item.label}
                placement="right"
                key={item.path || index}
              >
                <ListItemButton
                  onClick={() => handleMenuClick(item)}
                  selected={location.pathname === item.path}
                  sx={{
                    mb: 0.25,
                    mx: expanded ? 0 : 0,
                    py: 1,
                    borderRadius: "8px 0 0 8px",
                    backgroundColor:
                      location.pathname === item.path ? "rgba(255, 96, 52, 0.16)" : "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(255, 96, 52, 0.12)",
                      transform: expanded ? "translateX(4px)" : "none",
                      cursor: "pointer",
                    },
                    transition: "all 0.2s ease-in-out",
                    color: "#F1F2F3",
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    px: expanded ? 2 : 0,
                    borderRight:
                      location.pathname === item.path
                        ? "4px solid #ff6034"
                        : "4px solid transparent",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: location.pathname === item.path ? "#ff6034" : "#F1F2F3",
                      minWidth: expanded ? 40 : "auto",
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center",
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {expanded && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: "0.85rem",
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            ))}
          </List>
        </Box>

        {/* Settings Icon at bottom */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: expanded ? 240 : 60,
            display: "flex",
            flexDirection: expanded ? "row" : "column",
            justifyContent: "center",
            py: 2,
            background: "rgba(0, 0, 0, 0.2)",
            borderTop: "1px solid rgba(241, 242, 243, 0.08)",
            transition: "width 0.3s ease-in-out",
          }}
        >
          <Tooltip title="Contact Support" placement="right">
            <IconButton
              size="small"
              sx={{
                color: "#F1F2F3",
                padding: "4px",
                borderRadius: "4px",
                "&:hover": {
                  color: "#ff6034",
                  backgroundColor: "rgba(255, 96, 52, 0.12)",
                },
                mt: 0.5,
              }}
              onClick={handleContactSupport}
            >
              <ContactSupportIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Legal Disclaimer" placement="right">
            <IconButton
              size="small"
              sx={{
                color: "#F1F2F3",
                padding: "4px",
                borderRadius: "4px",
                "&:hover": {
                  color: "#ff6034",
                  backgroundColor: "rgba(255, 96, 52, 0.12)",
                },
                mt: 0.5,
              }}
              onClick={() => window.open("https://www.kearney.com/legal-disclaimer", "_blank")}
            >
              <GavelIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Privacy Policy" placement="right">
            <IconButton
              size="small"
              sx={{
                color: "#F1F2F3",
                padding: "4px",
                borderRadius: "4px",
                "&:hover": {
                  color: "#ff6034",
                  backgroundColor: "rgba(255, 96, 52, 0.12)",
                },
                mt: 0.5,
              }}
              onClick={() => window.open("https://www.kearney.com/privacy-policy", "_blank")}
            >
              <PrivacyTipIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Responsible AI Use" placement="right">
            <IconButton
              size="small"
              sx={{
                color: "#F1F2F3",
                padding: "4px",
                borderRadius: "4px",
                "&:hover": {
                  color: "#ff6034",
                  backgroundColor: "rgba(255, 96, 52, 0.12)",
                },
                mt: 0.8,
              }}
              onClick={() => window.open("https://www.kearney.com/responsible-ai-use", "_blank")}
            >
              <PsychologyIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Help" placement="right">
            <IconButton
              size="small"
              sx={{
                color: "#F1F2F3",
                padding: "4px",
                borderRadius: "4px",
                "&:hover": {
                  color: "#ff6034",
                  backgroundColor: "rgba(255, 96, 52, 0.12)",
                },
                mt: 0.8,
              }}
              onClick={handleOpenHelp}
            >
              <HelpOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Known Issues" placement="right">
            <IconButton
              size="small"
              sx={{
                color: "#F1F2F3",
                padding: "4px",
                borderRadius: "4px",
                "&:hover": {
                  color: "#ff6034",
                  backgroundColor: "rgba(255, 96, 52, 0.12)",
                },
                mt: 0.8,
              }}
              onClick={handleOpenKnownIssues}
            >
              <ReportProblemIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Settings" placement="right">
            <IconButton
              size="small"
              sx={{
                color: "#F1F2F3",
                borderRadius: "4px",
                "&:hover": {
                  color: "#ff6034",
                  backgroundColor: "rgba(255, 96, 52, 0.12)",
                },
                mt: 0.8,
              }}
              onClick={handleSettingsMenuOpen}
            >
              <SettingsIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Drawer>

      {/* Contact Support Dialog */}
      <Dialog
        open={supportDialogOpen}
        onClose={handleCloseSupportDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            backgroundColor: "#2c2c2c",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#fff",
            pb: 1,
          }}
        >
          Contact Support
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ color: "#fff", mb: 2 }}>
            Please describe your issue or question, and our support team will get back to you as
            soon as possible.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={6}
            fullWidth
            placeholder="Enter your message here..."
            value={supportMessage}
            onChange={(e) => setSupportMessage(e.target.value)}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#3a3a3a",
                color: "#fff",
                "&:hover fieldset": {
                  borderColor: "#ff6034",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ff6034",
                },
              },
              "& .MuiOutlinedInput-input": {
                color: "#fff",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#999",
                opacity: 1,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseSupportDialog}
            sx={{
              color: "#fff",
              backgroundColor: "#ff6034",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": {
                backgroundColor: "#e55530",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendSupportRequest}
            variant="contained"
            sx={{
              backgroundColor: "#ff6034",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": {
                backgroundColor: "#e55530",
              },
            }}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help GIF Player Overlay */}
      <Dialog
        open={helpDialogOpen}
        onClose={handleCloseHelp}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            width: "min(98vw, 1640px)",
            height: "92vh",
            border: "1px solid rgba(241, 242, 243, 0.1)",
            borderRadius: "10px",
            backgroundColor: "#202223",
            color: "#fff",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 18px 48px rgba(0, 0, 0, 0.42)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.1,
            px: 1.75,
            backgroundColor: "#1b1c1d",
            color: "#fff",
            borderBottom: "1px solid rgba(241, 242, 243, 0.1)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                backgroundColor: "rgba(255, 96, 52, 0.14)",
                color: "#ff6034",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.15 }}>
                Help
              </Typography>
              <Typography sx={{ mt: 0.15, color: "#A0A8AB", fontSize: "0.72rem" }}>
                Quick walkthroughs
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseHelp}
            size="small"
            sx={{
              color: "#fff",
              "&:hover": {
                color: "#ff6034",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
            aria-label="Close help"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, minHeight: 0, backgroundColor: "#202223" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "230px 1fr" },
              height: "100%",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                borderRight: { xs: 0, md: "1px solid rgba(241, 242, 243, 0.1)" },
                borderBottom: { xs: "1px solid rgba(241, 242, 243, 0.1)", md: 0 },
                backgroundColor: "#1b1c1d",
                p: 1,
                overflow: "auto",
              }}
            >
              {helpGifItems.map((item, index) => {
                const selected = index === selectedHelpGifIndex;

                return (
                  <Button
                    key={item.title}
                    fullWidth
                    onClick={() => handleSelectHelpGif(index)}
                    sx={{
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      flexDirection: "column",
                      textAlign: "left",
                      gap: 0.25,
                      px: 1.25,
                      py: 0.95,
                      mb: 0.45,
                      borderRadius: "7px",
                      color: selected ? "#F1F2F3" : "#A0A8AB",
                      backgroundColor: selected ? "rgba(255, 96, 52, 0.14)" : "transparent",
                      border: selected
                        ? "1px solid rgba(255, 96, 52, 0.32)"
                        : "1px solid transparent",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: selected
                          ? "rgba(255, 96, 52, 0.18)"
                          : "rgba(241, 242, 243, 0.06)",
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: "0.83rem", fontWeight: 700, lineHeight: 1.2 }}>
                      {index + 1}. {item.title}
                    </Typography>
                  </Button>
                );
              })}
            </Box>

            <Box
              sx={{
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#111",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.25,
                  px: 1.4,
                  py: 0.95,
                  borderBottom: "1px solid rgba(241, 242, 243, 0.1)",
                  backgroundColor: "#171819",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.96rem", fontWeight: 700 }}>
                    {selectedHelpGif?.title}
                  </Typography>
                  <Typography sx={{ mt: 0.12, color: "#A0A8AB", fontSize: "0.73rem" }}>
                    {selectedHelpGif?.description}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 0.75,
                  position: "relative",
                }}
              >
                {helpGifLoadError ? (
                  <Box sx={{ textAlign: "center", color: "#F1F2F3", maxWidth: 560 }}>
                    <Typography sx={{ fontSize: "1rem", fontWeight: 700, mb: 1 }}>
                      GIF not found
                    </Typography>
                    <Typography sx={{ color: "#A0A8AB", fontSize: "0.88rem" }}>
                      Place the file at
                      {" "}
                      <Box component="span" sx={{ color: "#F1F2F3", fontFamily: "monospace" }}>
                        costing_frontend/public{selectedHelpGif?.src}
                      </Box>
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box
                      component="img"
                      key={selectedHelpGifSrc}
                      src={selectedHelpGifSrc}
                      alt={selectedHelpGif?.title}
                      loading="eager"
                      decoding="sync"
                      fetchPriority="high"
                      onError={() => {
                        stopHelpGifProgress();
                        setHelpGifProgress(0);
                        setHelpGifLoadError(true);
                      }}
                      onLoad={() => {
                        setHelpGifLoadError(false);
                        startHelpGifProgress(0);
                      }}
                      sx={{
                        width: "100%",
                        height: "100%",
                        maxHeight: "100%",
                        display: "block",
                        objectFit: "contain",
                        imageRendering: "auto",
                        borderRadius: "7px",
                        backgroundColor: "#000",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        left: 8,
                        right: 8,
                        bottom: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={helpGifProgress}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 999,
                          backgroundColor: "rgba(241, 242, 243, 0.22)",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                            backgroundColor: "#ff6034",
                            transition: "none",
                          },
                        }}
                      />
                      <Tooltip title="Replay" placement="top">
                        <IconButton
                          onClick={handleReplayHelpGif}
                          size="small"
                          sx={{
                            width: 30,
                            height: 30,
                            color: "#F1F2F3",
                            backgroundColor: "rgba(17, 17, 17, 0.72)",
                            border: "1px solid rgba(241, 242, 243, 0.18)",
                            flexShrink: 0,
                            "&:hover": {
                              color: "#ff6034",
                              borderColor: "rgba(255, 96, 52, 0.5)",
                              backgroundColor: "rgba(255, 96, 52, 0.12)",
                            },
                          }}
                          aria-label="Replay help GIF"
                        >
                          <ReplayIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Known Issues Overlay */}
      <Dialog
        open={knownIssuesDialogOpen}
        onClose={handleCloseKnownIssues}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            border: "1px solid rgba(241, 242, 243, 0.1)",
            borderRadius: "10px",
            backgroundColor: "#202223",
            color: "#fff",
            overflow: "hidden",
            boxShadow: "0 18px 48px rgba(0, 0, 0, 0.42)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 2,
            px: 2.5,
            backgroundColor: "#1b1c1d",
            color: "#fff",
            fontWeight: 700,
            borderBottom: "1px solid rgba(241, 242, 243, 0.1)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "8px",
                backgroundColor: "rgba(255, 96, 52, 0.14)",
                color: "#ff6034",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ReportProblemIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, lineHeight: 1.2 }}>
                Known Issues
              </Typography>
              <Typography sx={{ mt: 0.35, color: "#A0A8AB", fontSize: "0.78rem" }}>
                Known behaviors to consider before proceeding
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseKnownIssues}
            size="small"
            sx={{
              color: "#fff",
              "&:hover": {
                color: "#ff6034",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
            aria-label="Close known issues"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2.75, backgroundColor: "#202223" }}>
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2.25,
              color: "#F1F2F3",
              "& li::marker": {
                color: "#ff6034",
                fontSize: "1.1rem",
              },
            }}
          >
            {knownIssues.map((issue) => (
              <Typography
                key={issue}
                component="li"
                sx={{
                  color: "#F1F2F3",
                  fontSize: "0.95rem",
                  lineHeight: 1.65,
                  fontWeight: 400,
                  mb: 1,
                  pl: 0.75,
                  "&:last-of-type": {
                    mb: 0,
                  },
                }}
              >
                {issue}
              </Typography>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* User Settings Dialog */}
      <Dialog
        open={userDetailsOpen}
        onClose={handleUserDetailsClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.35)",
            backgroundColor: "#1e1f20",
            color: "#fff",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "1.2rem",
            fontWeight: 600,
            color: "#fff",
            pb: 1,
          }}
        >
          Settings
          <IconButton
            onClick={handleUserDetailsClose}
            size="small"
            sx={{
              color: "#fff",
              "&:hover": { color: "#ff6034" },
            }}
          >
            <Typography sx={{ fontSize: "1.1rem" }}>×</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 600, mr: 2 }}>Profile</Typography>
            <Box sx={{ flex: 1 }} />
            <Avatar
              sx={{
                bgcolor: "#ff6034",
                color: "#000000",
                fontWeight: "bold",
                fontSize: "0.85rem",
                width: 36,
                height: 36,
              }}
            >
              {userInfo.initials || "U"}
            </Avatar>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, color: "#fff" }}>Role</Typography>
            <Typography sx={{ color: "#9aa1a6" }}>{userInfo.role || "N/A"}</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, color: "#fff" }}>Name</Typography>
            <Typography sx={{ color: "#9aa1a6" }}>
              {[user?.firstName, user?.lastName].filter(Boolean).join(" ") || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, color: "#fff" }}>Email</Typography>
            <Typography sx={{ color: "#9aa1a6" }}>
              {userInfo.email || user?.email || "N/A"}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={supportNotification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={supportNotification.severity}
          sx={{ width: "100%" }}
        >
          {supportNotification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Sidebar;
